import { ReactNode, Suspense, lazy, useEffect, useLayoutEffect, useState } from 'react';
import {
  createHashRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { ErrorPage } from '@/pages/ErrorPage';
import { ErrorBoundary } from 'react-error-boundary';
import { useAnalytics } from '@/hooks/useAnalytics';
import { captureOAuthParams, isSsoRedirectPending } from '@asi-lda/pnet-auth-web';
import { ApiProvider } from '@/provider/ApiProvider';
import { Hub } from 'aws-amplify/utils';
import { hasSessionHint } from '@/utils/accessTokenCopy';

type AuthHubPayload = {
  event: string;
  data?: unknown;
};

// Capture OAuth callback params before Amplify or re-renders can clear them.
// Cognito cannot redirect to hash URLs (e.g. /#/sso), so it redirects to the
// plain origin. We detect the params here and forward them into the hash router.
// `captureOAuthParams` reads the query string AND a hash-router query, and
// counts only `code`/`error` as a callback: `state` alone is NOT one - Cognito's
// /logout endpoint redirects back with only ?state=xxx, and treating that as a
// callback trapped the user in a / -> /sso -> Back to Login -> / loop.
const _hasOAuthCallbackParams = captureOAuthParams().hasCallbackParams;

const LoginPage = lazy(() => import('@/pages/login'));
const ResetPasswordPage = lazy(() => import('@/pages/resetpassword'));
const SSOLogin = lazy(() => import('@/pages/sso').then((module) => ({ default: module.SSOLogin })));
const SecurityPage = lazy(() => import('@/pages/security'));

const BrandBonusComponent = lazy(() => import('@/components/BrandProfile/Brandbonus'));
const LoginPreferences = lazy(() => import('@/components/BrandProfile/LoginPreferences'));
const TermsPage = lazy(() => import('@/pages/Termsofuse'));
const ActivationFormPage = lazy(() => import('@/pages/activation'));
const AddActivityPage = lazy(() => import('@/pages/addActivity'));
const AddActivityProductLaunchPage = lazy(() => import('@/pages/addActivityProductLaunch'));
const ProductLaunchStepperPage = lazy(() => import('@/pages/productLaunchStepperPage'));
const ViewActivityPage = lazy(() => import('@/pages/viewActivity'));
const AddBrand = lazy(() => import('@/pages/addbrand'));
const AddProperty = lazy(() => import('@/pages/addproperty'));
const AudienceInterestPage = lazy(() => import('@/pages/audience_interest'));
const BrandProfilePage = lazy(() => import('@/pages/brandprofile'));
const BrandDetailOverview = lazy(() => import('@/pages/BrandDetailOverview'));
const StoreCampaign = lazy(() => import('@/pages/campaigns/storeCampaign'));
const ViewCampaign = lazy(() => import('@/pages/campaigns/viewCampaign'));
const ContractFormPage = lazy(() => import('@/pages/contract'));
const LightContractPage = lazy(() => import('@/pages/lightContract'));
const ContractViewPage = lazy(() => import('@/pages/contractview'));
const CurrencyExchangePage = lazy(() => import('@/pages/currencyexchangerate'));
const DashboardPage = lazy(() => import('@/pages/dashboard'));
const DbsPage = lazy(() => import('@/pages/dbs'));
const EditAndViewActivationPage = lazy(() => import('@/pages/editandviewactivation'));
const HeadToHeadPage = lazy(() => import('@/pages/headtohead'));
const ListViewPage = lazy(() => import('@/pages/listview'));
const NoPermissionPage = lazy(() => import('@/pages/nopermission'));
const ProfilePage = lazy(() => import('@/pages/propertyprofile'));
const RevenueOutput = lazy(() => import('@/pages/revenueoutput'));
const RolePermissionPage = lazy(() => import('@/pages/rolepermission'));
const ReportsPage = lazy(() => import('@/pages/reports'));
const StrategicFitPage = lazy(() => import('@/pages/strategicfit'));
const UserManagementPage = lazy(() => import('@/pages/usermanagement'));
const UserNewPage = lazy(() => import('@/pages/usernewpage'));
const UserRoleListPage = lazy(() => import('@/pages/userrolelist'));
const WorkInProgressPage = lazy(() => import('@/pages/workinprogress'));
const ThemeEditForm = lazy(() => import('@/components/shared/Forms/ThemeEdit/ThemeEditForm'));
const EmailTemplates = lazy(() => import('@/pages/emailtemplates'));
const AddEmailTemplates = lazy(() => import('@/pages/addemailtemplates'));
const EditEmailTemplate = lazy(() => import('@/pages/editemailtemplate'));
const AddActivation = lazy(() => import('@/pages/addActivation/addActivation'));
const ViewActivation = lazy(() => import('@/pages/ViewActivation'));
const AthleteProfile = lazy(() => import('@/pages/AthleteProfile'));
const ActivationSelectionPage = lazy(() => import('@/pages/comparison/ActivationSelectionPage'));
const PartnerSelectionPage = lazy(() => import('@/pages/comparison/PartnerSelectionPage'));
const ComparisonPage = lazy(() => import('@/pages/comparison/ComparisonPage'));
const CompetitionPage = lazy(() => import('@/pages/comparison/CompetitionPage'));
const RetrospectivePage = lazy(() => import('@/pages/comparison/RetrospectivePage'));
const CreateActivationPage = lazy(() => import('@/pages/comparison/CreateActivationPage'));
const DueDiligenceWizard = lazy(() => import('@/pages/due-diligence/DueDiligenceWizard'));
const DashboardExecutivePage = lazy(() => import('@/pages/dashboard-executive'));
const DashboardDetailedPage = lazy(() => import('@/pages/dashboard-detailed'));
const RunningCalculationsPage = lazy(() => import('@/pages/running-calculations'));
const ExpertsPage = lazy(() => import('@/pages/experts'));
const GovernancePage = lazy(() => import('@/pages/governance'));
const RightsPage = lazy(() => import('@/pages/rights'));
const BenchmarkGroupsPage = lazy(() => import('@/pages/benchmark-groups'));
const BrandOnboardingPage = lazy(() => import('@/pages/brand-onboarding'));
const PartnerOnboardingPage = lazy(() => import('@/pages/partner-onboarding'));
const DecisionGymPage = lazy(() => import('@/pages/decision-gym'));
const AIPPTCreatorPage = lazy(() => import('@/pages/decision-gym/AIPPTCreator'));
const InvestmentBriefPage = lazy(() => import('@/pages/investment-brief'));
const RouteLoadingFallback = () => <div className='main-loader-area' />;

// ... (your other imports)
const isAuthenticated = () => {
  const userData = localStorage.getItem('userData');
  return Boolean(userData && hasSessionHint());
};

// Higher-Order Component for Authenticated routes
const AuthenticatedRoute = ({ element }: { element: ReactNode }) => {
  if (isAuthenticated()) {
    return (
      <ErrorBoundary FallbackComponent={ErrorPage}>
        <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>
      </ErrorBoundary>
    );
  }
  return <Navigate to='/' />;
};

// Higher-Order Component for Unauthenticated routes
const UnauthenticatedRoute = ({ element }: { element: ReactNode }) => {
  if (!isAuthenticated()) {
    return <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>;
  }
  return <Navigate to='/dashboard' />;
};

// Root Layout component for analytics
function RootLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  useAnalytics();
  const isRootLoginPath = location.pathname === '/';
  const shouldRouteToSso = isRootLoginPath && (_hasOAuthCallbackParams || isSsoRedirectPending());

  // Forward Cognito OAuth callback params into the hash router so SSOLogin
  // can handle them. Runs before browser paint to avoid a flash of login page.
  useLayoutEffect(() => {
    if (shouldRouteToSso && location.pathname !== '/sso') {
      // Keep OAuth params in browser search (window.location.search) so Amplify
      // can complete the code exchange reliably, and only move hash-route to /sso.
      navigate('/sso', { replace: true });
    }
  }, [location.pathname, navigate, shouldRouteToSso]);

  // Hub listener: backup SSO detection when Amplify clears URL params before
  // _oauthCode is captured above (race condition with enable-oauth-listener).
  // signInWithRedirect fires AFTER code exchange completes — SSOLogin's
  // getSessionIdTokenWithRetry() will find the session immediately.
  useEffect(() => {
    const remove = Hub.listen('auth', ({ payload }: { payload: AuthHubPayload }) => {
      if (payload.event === 'signInWithRedirect') {
        navigate('/sso', { replace: true });
      } else if (payload.event === 'signInWithRedirect_failure') {
        const err = (payload.data as any)?.error;
        const desc = err?.message || String(err || 'SSO authentication failed');
        navigate(`/sso?error=oauth_error&error_description=${encodeURIComponent(desc)}`, {
          replace: true,
        });
      }
    });
    return remove;
  }, [navigate]);

  // Validate local session against the backend on route transitions so stale
  // tabs/devices are redirected before expensive page actions start firing.
  useEffect(() => {
    if (location.pathname === '/sso') return;
    if (!isAuthenticated()) return;
    void ApiProvider.ensureSessionAlive(false);
  }, [location.pathname, location.search, location.hash]);

  // Also re-check when the tab/window becomes active again. This covers the
  // "Sign out all devices" case while a page is left open in the background.
  useEffect(() => {
    const probe = () => {
      if (window.location.hash.includes('/sso')) return;
      if (!isAuthenticated()) return;
      void ApiProvider.ensureSessionAlive(true);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        probe();
      }
    };

    window.addEventListener('focus', probe);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', probe);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  if (shouldRouteToSso && location.pathname !== '/sso') {
    return <RouteLoadingFallback />;
  }

  return <Outlet />;
}

const ContractRoute = () => {
  const [hasPermission, setHasPermission] = useState(false);

  useEffect(() => {
    setHasPermission(ApiProvider.permission('contract_light'));
  }, []);

  return hasPermission ? <LightContractPage /> : <ContractFormPage />;
};

const router = createHashRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      {/* ---------------------- UNAUTHENTICATED ROUTES ---------------------- */}
      <Route
        path='/sso'
        element={
          <Suspense fallback={<RouteLoadingFallback />}>
            <SSOLogin />
          </Suspense>
        }
      />
      <Route
        path='/resetpassword'
        element={<UnauthenticatedRoute element={<ResetPasswordPage />} />}
      />
      <Route path='/' element={<UnauthenticatedRoute element={<LoginPage />} />} />

      {/* ---------------------- AUTHENTICATED ROUTES ---------------------- */}
      <Route path='/dashboard' element={<AuthenticatedRoute element={<DashboardPage />} />} />
      <Route path='/decision-gym' element={<AuthenticatedRoute element={<DecisionGymPage />} />} />
      <Route
        path='/decision-gym/ai-ppt-creator'
        element={<AuthenticatedRoute element={<AIPPTCreatorPage />} />}
      />
      <Route
        path='/partnership-due-diligence'
        element={<AuthenticatedRoute element={<DueDiligenceWizard />} />}
      />
      <Route
        path='/investment-brief'
        element={<AuthenticatedRoute element={<InvestmentBriefPage />} />}
      />
      <Route
        path='/running-calculations'
        element={<AuthenticatedRoute element={<RunningCalculationsPage />} />}
      />
      <Route
        path='/dashboard-executive'
        element={<AuthenticatedRoute element={<DashboardExecutivePage />} />}
      />
      <Route
        path='/dashboard-detailed'
        element={<AuthenticatedRoute element={<DashboardDetailedPage />} />}
      />
      <Route
        path='/loginpreferences'
        element={<AuthenticatedRoute element={<LoginPreferences />} />}
      />
      <Route path='/security' element={<AuthenticatedRoute element={<SecurityPage />} />} />
      <Route path='/experts' element={<AuthenticatedRoute element={<ExpertsPage />} />} />
      <Route path='/governance' element={<AuthenticatedRoute element={<GovernancePage />} />} />
      <Route path='/rights' element={<AuthenticatedRoute element={<RightsPage />} />} />
      <Route
        path='/benchmark-groups'
        element={<AuthenticatedRoute element={<BenchmarkGroupsPage />} />}
      />
      <Route
        path='/brand-onboarding'
        element={<AuthenticatedRoute element={<BrandOnboardingPage />} />}
      />
      <Route
        path='/partner-onboarding'
        element={<AuthenticatedRoute element={<PartnerOnboardingPage />} />}
      />

      <Route path='/theme/edit' element={<AuthenticatedRoute element={<ThemeEditForm />} />} />

      <Route path='/emailtemplates' element={<AuthenticatedRoute element={<EmailTemplates />} />} />
      <Route
        path='/addemailtemplate'
        element={<AuthenticatedRoute element={<AddEmailTemplates />} />}
      />
      <Route
        path='/emailtemplates/:id/edit'
        element={<AuthenticatedRoute element={<EditEmailTemplate />} />}
      />

      <Route
        path='/brandbonus'
        element={<AuthenticatedRoute element={<BrandBonusComponent />} />}
      />
      <Route
        path='/strategicfit/:id/:email'
        element={<AuthenticatedRoute element={<StrategicFitPage />} />}
      />
      <Route path='/Termsofuse' element={<AuthenticatedRoute element={<TermsPage />} />} />
      <Route
        path='/workinprogress'
        element={<AuthenticatedRoute element={<WorkInProgressPage />} />}
      />
      <Route path='/nopermission' element={<AuthenticatedRoute element={<NoPermissionPage />} />} />
      <Route path='/dbs' element={<AuthenticatedRoute element={<DbsPage />} />} />
      <Route
        path='/currencyexchange'
        element={<AuthenticatedRoute element={<CurrencyExchangePage />} />}
      />
      <Route path='/headtohead' element={<AuthenticatedRoute element={<HeadToHeadPage />} />} />
      <Route
        path='/revenueoutput/:id'
        element={<AuthenticatedRoute element={<RevenueOutput />} />}
      />
      <Route
        path='/audienceinterest'
        element={<AuthenticatedRoute element={<AudienceInterestPage />} />}
      />

      {/* User Management */}
      <Route path='/adduser' element={<AuthenticatedRoute element={<UserNewPage />} />} />
      <Route path='/userlist' element={<AuthenticatedRoute element={<UserManagementPage />} />} />
      <Route path='/adduser/:type/:id' element={<AuthenticatedRoute element={<UserNewPage />} />} />
      <Route path='/userrolelist' element={<AuthenticatedRoute element={<UserRoleListPage />} />} />
      <Route path='/reports' element={<AuthenticatedRoute element={<ReportsPage />} />} />
      <Route
        path='/rolepermission/:pagetype/:id'
        element={<AuthenticatedRoute element={<RolePermissionPage />} />}
      />

      {/* Manage Brands, Properties, Campaigns (New: Activations), Activations (New: Activities) and Contracts */}
      <Route
        path='/listview/:pageName'
        element={<AuthenticatedRoute element={<ListViewPage />} />}
      />

      <Route path='/addbrand' element={<AuthenticatedRoute element={<AddBrand />} />} />
      <Route
        path='/brandprofile/:id'
        element={<AuthenticatedRoute element={<BrandProfilePage />} />}
      />
      <Route
        path='/brandprofile/:id/:flag'
        element={<AuthenticatedRoute element={<BrandProfilePage />} />}
      />

      <Route
        path='/brand-overview/:id'
        element={<AuthenticatedRoute element={<BrandDetailOverview />} />}
      />

      <Route path='/addproperty' element={<AuthenticatedRoute element={<AddProperty />} />} />
      <Route
        path='/partnerProfile/:propertyId'
        element={<AuthenticatedRoute element={<AthleteProfile />} />}
      />
      <Route
        path='/partnerProfile/:propertyId/:brandId'
        element={<AuthenticatedRoute element={<AthleteProfile />} />}
      />
      <Route
        path='/propertyprofile/:id/:flag'
        element={<AuthenticatedRoute element={<ProfilePage />} />}
      />
      <Route
        path='/propertyprofile/:id'
        element={<AuthenticatedRoute element={<ProfilePage />} />}
      />

      <Route path='/addActivation' element={<AuthenticatedRoute element={<AddActivation />} />} />
      <Route
        path='/viewActivation/:id'
        element={<AuthenticatedRoute element={<ViewActivation />} />}
      />

      <Route path='/storecampaign' element={<AuthenticatedRoute element={<StoreCampaign />} />} />
      <Route
        path='/storecampaign/:id'
        element={<AuthenticatedRoute element={<StoreCampaign />} />}
      />
      <Route path='/viewcampaign/:id' element={<AuthenticatedRoute element={<ViewCampaign />} />} />

      <Route
        path='/activationform/:idtype/:id'
        element={<AuthenticatedRoute element={<ActivationFormPage />} />}
      />
      <Route
        path='/activationform'
        element={<AuthenticatedRoute element={<ActivationFormPage />} />}
      />
      <Route path='/addActivity' element={<AuthenticatedRoute element={<AddActivityPage />} />} />
      <Route
        path='/addActivity/:activationId'
        element={<AuthenticatedRoute element={<AddActivityProductLaunchPage />} />}
      />
      <Route
        path='/productLaunch/:activationId'
        element={<AuthenticatedRoute element={<ProductLaunchStepperPage />} />}
      />
      <Route
        path='/editandviewactivation/:id'
        element={<AuthenticatedRoute element={<EditAndViewActivationPage />} />}
      />
      <Route
        path='/viewActivity/:id'
        element={<AuthenticatedRoute element={<ViewActivityPage />} />}
      />
      <Route
        path='/addcontract/:idType/:id'
        element={<AuthenticatedRoute element={<ContractRoute />} />}
      />
      <Route path='/addcontract' element={<AuthenticatedRoute element={<ContractRoute />} />} />
      <Route
        path='/viewcontract/:id'
        element={<AuthenticatedRoute element={<ContractViewPage />} />}
      />

      {/* ---------------------- COMPARISON ROUTE ---------------------- */}
      {/* Entry point: Choose Activation */}
      <Route
        path='/comparison'
        element={<AuthenticatedRoute element={<ActivationSelectionPage />} />}
      />
      {/* Step 2: Select Brands for an activation */}
      <Route
        path='/comparison/activation/:id'
        element={<AuthenticatedRoute element={<PartnerSelectionPage />} />}
      />
      {/* Direct comparison view (with brands pre-selected via URL params) */}
      <Route
        path='/comparison/view'
        element={<AuthenticatedRoute element={<ComparisonPage />} />}
      />
      <Route
        path='/comparison/competition'
        element={<AuthenticatedRoute element={<CompetitionPage />} />}
      />
      <Route
        path='/comparison/retrospective'
        element={<AuthenticatedRoute element={<RetrospectivePage />} />}
      />
      <Route
        path='/comparison/create-activation'
        element={<AuthenticatedRoute element={<CreateActivationPage />} />}
      />

      <Route path='*' element={<RedirectToHome />} />
    </Route>,
  ),
);

const Index = () => {
  return <RouterProvider router={router} />;
};

function RedirectToHome() {
  const navigate = useNavigate();

  useEffect(() => {
    navigate('/');
  }, [navigate]);

  return null;
}

export default Index;
